import { useCallback, useEffect, useRef, useState } from 'react';
import { shouldPromptRating } from '@mobi/shared';
import { api } from './api.js';

/** O mínimo que a última corrida precisa ter para decidir a tela. */
export interface RatableRide {
  id: number;
  status: string;
  rated?: boolean;
}

/**
 * Última corrida concluída e ainda não avaliada por quem está usando o app.
 *
 * Vale para passageiro e motorista: os dois avaliam, os dois podem fechar
 * o app antes de avaliar, e nos dois casos a tela precisa voltar sozinha
 * na reabertura — e sair de vez depois da nota.
 */
export function useLastUnratedRide<T extends RatableRide>(activeRideId: number | null) {
  const [pending, setPending] = useState<T | null>(null);
  // Ref e não state: dispensar não deve, por si, renderizar de novo nem
  // entrar na dependência do check.
  const skipped = useRef<number | null>(null);

  const recheck = useCallback(async () => {
    try {
      const res = await api<{ rides: T[] }>('/api/rides?limit=1');
      const last = res.rides[0];
      if (!shouldPromptRating(last, skipped.current)) {
        setPending(null);
        return;
      }
      /*
       * A listagem não traz as partes — /api/rides usa present() sem
       * elas, de propósito, para o histórico não carregar nome, veículo e
       * foto de todo mundo. A tela de avaliação precisa disso, então
       * busca a corrida inteira pelo id. Uma requisição a mais, e só
       * quando há avaliação pendente.
       */
      try {
        setPending(await api<T>(`/api/rides/${last!.id}`));
      } catch {
        // A corrida existe; só as partes não vieram. Avaliar sem o nome
        // do outro é melhor do que não conseguir avaliar.
        setPending(last!);
      }
    } catch {
      setPending(null);
    }
  }, []);

  /**
   * O id é explícito porque quem dispensa nem sempre é a corrida que este
   * hook trouxe — o motorista chega na tela pelo resultado do POST de
   * encerramento. Sem o id, o recheck seguinte traria a corrida de volta.
   */
  const dismiss = useCallback(
    (id?: number) => {
      const target = id ?? pending?.id;
      if (target !== undefined) skipped.current = target;
      setPending(null);
    },
    [pending],
  );

  useEffect(() => {
    if (activeRideId === null) void recheck();
  }, [activeRideId, recheck]);

  return { pending, recheck, dismiss };
}
